import React, { useEffect, useState, useCallback } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Process from './components/Process';
import Testimonial from './components/Testimonial';
import Footer from './components/Footer';
import ComingSoon from './components/ComingSoon';
import './App.css';

function App() {
  const [showComingSoon, setShowComingSoon] = useState(false);
  
  const handleShowComingSoon = useCallback(() => {
    setShowComingSoon(true);
    // Scroll to top when opening Coming Soon page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const closeMobileMenu = useCallback(() => {
    // Close menu immediately without delay
    const slideBar = document.querySelector('.slide-bar');
    const bodyOverlay = document.querySelector('.body-overlay');
    const body = document.body;
    const hamburgers = document.querySelectorAll('.hamburger_menu');
    
    if (slideBar) {
      slideBar.classList.remove('show');
    }
    if (body) {
      body.classList.remove('on-side');
    }
    if (bodyOverlay) {
      bodyOverlay.classList.remove('active');
    }
    hamburgers.forEach((hamburger) => {
      hamburger.classList.remove('active');
    });
  }, []);

  const handleNavigate = useCallback((target) => {
    // Close mobile menu immediately before navigation
    closeMobileMenu();
    
    // Close Coming Soon page first
    setShowComingSoon(false);
    
    // Wait for React to unmount Coming Soon and remount main content
    setTimeout(() => {
      // Clean up any Swiper instances that might be causing issues
      if (window.Swiper) {
        try {
          const swiperElements = document.querySelectorAll('.swiper-container, .swiper');
          swiperElements.forEach((el) => {
            if (el.swiper && el.swiper.destroy) {
              try {
                el.swiper.destroy(true, true);
              } catch (e) {
                // Ignore destroy errors
              }
            }
          });
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      
      // Wait a bit more for DOM to settle, then scroll
      setTimeout(() => {
        if (target === 'home' || target === '/') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const element = document.querySelector(target);
          if (element) {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      }, 200);
    }, 150);
  }, [closeMobileMenu]);

  useEffect(() => {
    // Hide preloader immediately and ensure it stays hidden
    const hidePreloader = () => {
      const preloader = document.getElementById('xb-loadding');
      if (preloader) {
        preloader.style.display = 'none';
        preloader.style.visibility = 'hidden';
        preloader.style.opacity = '0';
      }
    };
    
    hidePreloader();
    
    // Fallback: hide preloader after a short delay
    const timeout = setTimeout(() => {
      hidePreloader();
    }, 100);

    // Don't load jQuery scripts if Coming Soon page is shown
    if (showComingSoon) {
      return () => clearTimeout(timeout);
    }

    document.body.classList.add('home-dark');

    const publicUrl = process.env.PUBLIC_URL || '';
    const scripts = [
      `${publicUrl}/Helax/assets/js/jquery-3.5.1.min.js`,
      `${publicUrl}/Helax/assets/js/bootstrap.bundle.min.js`,
      `${publicUrl}/Helax/assets/js/swiper.min.js`,
      `${publicUrl}/Helax/assets/js/wow.min.js`,
      `${publicUrl}/Helax/assets/js/appear.js`,
      `${publicUrl}/Helax/assets/js/jquery.magnific-popup.min.js`,
      `${publicUrl}/Helax/assets/js/metisMenu.min.js`,
      `${publicUrl}/Helax/assets/js/cursor.js`,
      `${publicUrl}/Helax/assets/js/jquery.marquee.min.js`,
      `${publicUrl}/Helax/assets/js/parallax-scroll.js`,
      `${publicUrl}/Helax/assets/js/easing.min.js`,
      `${publicUrl}/Helax/assets/js/scrollspy.js`,
      `${publicUrl}/Helax/assets/js/main.js`
    ];

    // Prevent multiple initializations
    if (window.jQuery && window.jQueryInitialized) {
      return () => clearTimeout(timeout);
    }

    const loadScript = (index) => {
      if (index >= scripts.length) {
        // Ensure preloader is hidden
        hidePreloader();
        
        if (window.WOW && !window.WOWInitialized) {
          try {
            new window.WOW().init();
            window.WOWInitialized = true;
          } catch (e) {
          }
        }
        
        window.jQueryInitialized = true;
        return;
      }

      const src = scripts[index];
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        // Script already exists, move to next
        setTimeout(() => loadScript(index + 1), 0);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () => {
        loadScript(index + 1);
      };
      script.onerror = () => {
        loadScript(index + 1);
      };
      document.head.appendChild(script);
    };

    // Start loading scripts
    if (!window.jQuery) {
      loadScript(0);
    } else if (!window.jQueryInitialized) {
      // jQuery is loaded but main.js might not be
      const mainJsIndex = scripts.findIndex(s => s.includes('main.js'));
      if (mainJsIndex !== -1 && !document.querySelector(`script[src="${scripts[mainJsIndex]}"]`)) {
        loadScript(mainJsIndex);
      } else {
        window.jQueryInitialized = true;
      }
    }
    
    return () => {
      clearTimeout(timeout);
      if (!showComingSoon) {
        document.body.classList.remove('home-dark');
      }
    };

  }, [showComingSoon]);

  return (
    <div className="body_wrap">
      {/* loaders / cursor */}
      <div id="xb-loadding" className="xb-loader style-2">
        <div className="xb-dual-ring"></div>
      </div>

      <div className="xb-cursor tx-js-cursor style-2">
        <div className="xb-cursor-wrapper">
          <div className="xb-cursor--follower xb-js-follower"></div>
        </div>
      </div>

      <div className="progress-wrap style-2">
        <svg className="progress-circle svg-content" width="100%" height="100%" viewBox="-1 -1 102 102">
          <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
        </svg>
      </div>

      <Header 
        onLaunchApp={handleShowComingSoon} 
        onShowComingSoon={handleShowComingSoon}
        onNavigate={handleNavigate}
        showComingSoon={showComingSoon}
      />

      <aside className="slide-bar">
        <nav className="main-menu">
          <ul>
            <li><a className="scrollspy-btn" href="/" onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeMobileMenu(); handleNavigate('home'); }}>Home</a></li>
            <li><a className="scrollspy-btn" href="#feature" onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeMobileMenu(); handleNavigate('#feature'); }}>Feature</a></li>
            <li><a className="scrollspy-btn" href="#process" onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeMobileMenu(); handleNavigate('#process'); }}>What is Nova Verse</a></li>
            <li><a className="scrollspy-btn" href="/" onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeMobileMenu(); handleShowComingSoon(); }}>Docs</a></li>
          </ul>
        </nav>
      </aside>
      <div 
        className="body-overlay"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          
          // Prevent jQuery from handling this
          if (window.jQuery) {
            window.jQuery(e.currentTarget).off('click');
          }
          
          closeMobileMenu();
        }}
        onMouseDown={(e) => {
          // Prevent any other handlers
          e.stopPropagation();
        }}
      ></div>

      <main>
        {showComingSoon ? (
          <ComingSoon />
        ) : (
          <>
            <Hero onShowComingSoon={handleShowComingSoon} />
            <Features />
            <Process onShowComingSoon={handleShowComingSoon} />
            <Testimonial />
          </>
        )}
      </main>

      <Footer onShowComingSoon={handleShowComingSoon} />
    </div>
  );
}

export default App;
